/**
 * /features/:slug — dedicated marketing page for a single product feature.
 *
 * Reads from `lib/features.ts` so all the copy lives in a single declarative
 * place; the layout here is generic. Sections fold automatically when the
 * feature data leaves a block empty — that lets us ship the whole catalog with
 * placeholder copy and iterate the long-form copy feature-by-feature.
 *
 * Sections rendered (in order):
 *  - Hero (eyebrow + title + description + CTA + mockup)
 *  - "El problema" (problem statement)
 *  - "Cómo funciona" (numbered steps)
 *  - "Por qué importa" (benefits grid)
 *  - "FAQ" (accordion)
 *  - Related features (3 cards) + final CTA strip
 */
import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import {
  ArrowRight,
  ChevronDown,
  CheckCircle2,
  Sparkles,
  Home,
} from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { SiteFooter } from '../components/SiteFooter';
import { getFeatureBySlug, FEATURES, type Feature } from '../lib/features';

export function FeaturePage() {
  const { slug } = useParams<{ slug: string }>();
  const feature = slug ? getFeatureBySlug(slug) : undefined;

  // Client-side <title> + meta description (the server inyecta los mismos
  // valores antes de servir el SPA para crawlers; aquí sólo cubrimos navegación
  // SPA, donde el server-side no se ejecuta).
  useEffect(() => {
    if (!feature) return;
    const prevTitle = document.title;
    const seoTitle = feature.seo?.title ?? `${feature.title} · Zettapost`;
    document.title = seoTitle;
    return () => {
      document.title = prevTitle;
    };
  }, [feature]);

  if (!feature) return <Navigate to="/features" replace />;

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Navbar />

      {/* ── Hero ───────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-brand-50/40 to-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-12 lg:py-20">
          <Breadcrumbs feature={feature} />
          <div className="mt-6 grid lg:grid-cols-[1fr_1fr] gap-10 lg:gap-14 items-center">
            <div>
              <div className="inline-flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-700">
                  {feature.eyebrow}
                </span>
                {feature.badge && (
                  <span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-r from-amber-500 to-pink-500 text-white">
                    {feature.badge}
                  </span>
                )}
              </div>
              <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-slate-900">
                {feature.title}
              </h1>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-xl">
                {feature.description}
              </p>
              {feature.cta && (
                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <Link
                    to={feature.cta.href}
                    className="btn-primary inline-flex items-center gap-1.5"
                  >
                    {feature.cta.label}
                    <ArrowRight size={15} />
                  </Link>
                  <Link
                    to="/features"
                    className="text-sm text-slate-500 hover:text-slate-900 transition"
                  >
                    Ver todas las features →
                  </Link>
                </div>
              )}
            </div>

            {/* Mockup */}
            {feature.Mockup && (
              <div className="relative h-[280px] sm:h-[340px] lg:h-[360px] rounded-2xl bg-white border border-slate-200 shadow-sm p-5 lg:p-7 overflow-hidden">
                <feature.Mockup />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Problema ───────────────────────────────────────── */}
      {feature.sections?.problem && (
        <Section>
          <div className="max-w-3xl">
            <Eyebrow>El problema que resolvemos</Eyebrow>
            <h2 className="mt-3 text-2xl lg:text-3xl font-bold text-slate-900">
              {feature.sections.problem.title}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed text-lg">
              {feature.sections.problem.body}
            </p>
          </div>
        </Section>
      )}

      {/* ── Cómo funciona ──────────────────────────────────── */}
      {feature.sections?.howItWorks && feature.sections.howItWorks.steps.length > 0 && (
        <Section tone="muted">
          <Eyebrow>Cómo funciona</Eyebrow>
          <h2 className="mt-3 text-2xl lg:text-3xl font-bold text-slate-900">
            {feature.sections.howItWorks.title}
          </h2>
          <ol className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {feature.sections.howItWorks.steps.map((step, i) => (
              <li
                key={i}
                className="rounded-2xl bg-white border border-slate-200 p-5"
              >
                <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-700 font-bold text-sm">
                  {i + 1}
                </div>
                <h3 className="mt-3 font-semibold text-slate-900">{step.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{step.body}</p>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* ── Beneficios ─────────────────────────────────────── */}
      {feature.sections?.benefits && feature.sections.benefits.items.length > 0 && (
        <Section>
          <Eyebrow>Por qué importa</Eyebrow>
          <h2 className="mt-3 text-2xl lg:text-3xl font-bold text-slate-900">
            {feature.sections.benefits.title}
          </h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {feature.sections.benefits.items.map((b, i) => (
              <div
                key={i}
                className="rounded-2xl bg-gradient-to-br from-brand-50/50 to-white border border-slate-200 p-5"
              >
                <CheckCircle2 size={22} className="text-brand-600" />
                <h3 className="mt-3 font-semibold text-slate-900">{b.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ── FAQ ────────────────────────────────────────────── */}
      {feature.sections?.faq && feature.sections.faq.length > 0 && (
        <Section tone="muted">
          <div className="max-w-3xl">
            <Eyebrow>Preguntas frecuentes</Eyebrow>
            <h2 className="mt-3 text-2xl lg:text-3xl font-bold text-slate-900">
              Lo que la gente nos pregunta sobre {feature.title.toLowerCase()}
            </h2>
            <div className="mt-8 space-y-3">
              {feature.sections.faq.map((f, i) => (
                <details
                  key={i}
                  className="group rounded-xl bg-white border border-slate-200 overflow-hidden"
                >
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 px-5 py-4 font-semibold text-slate-900 hover:bg-slate-50 transition">
                    {f.q}
                    <ChevronDown
                      size={16}
                      className="text-slate-400 transition-transform group-open:rotate-180 shrink-0"
                    />
                  </summary>
                  <div className="px-5 pb-4 text-slate-600 leading-relaxed">{f.a}</div>
                </details>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* ── All other features + CTA final ────────────────── */}
      <Section tone="muted">
        <Eyebrow>Explora Zettapost</Eyebrow>
        <h2 className="mt-3 text-2xl lg:text-3xl font-bold text-slate-900">
          Todo lo que puedes hacer con Zettapost
        </h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.filter((f) => f.slug !== feature.slug).map((other) => {
            const Icon = other.icon;
            return (
              <Link
                key={other.slug}
                to={`/features/${other.slug}`}
                className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-brand-300 hover:shadow-md transition"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-700 inline-flex items-center justify-center">
                    <Icon size={18} />
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">
                    {other.eyebrow}
                  </div>
                </div>
                <h3 className="mt-3 font-semibold text-slate-900 group-hover:text-brand-700 transition">
                  {other.title}
                </h3>
                <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">
                  {other.shortDescription}
                </p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-brand-600 group-hover:text-brand-700">
                  Conocer más
                  <ArrowRight size={13} className="transition group-hover:translate-x-0.5" />
                </span>
              </Link>
            );
          })}
        </div>
      </Section>

      <Section>
        <div className="rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 text-white p-8 lg:p-12 text-center">
          <Sparkles size={28} className="mx-auto opacity-90" />
          <h2 className="mt-3 text-2xl lg:text-3xl font-bold">
            Empieza con 3 días gratis. Cancela cuando quieras.
          </h2>
          <p className="mt-2 text-brand-100 max-w-xl mx-auto">
            Conecta tus redes, prueba esta y todas nuestras features. Si no te convence, no pasa nada.
          </p>
          <Link
            to="/register"
            className="mt-6 inline-flex items-center gap-1.5 rounded-lg bg-white text-brand-700 font-semibold px-5 py-2.5 hover:bg-brand-50 transition"
          >
            Crear cuenta gratis
            <ArrowRight size={15} />
          </Link>
        </div>
      </Section>
      <SiteFooter />
    </div>
  );
}

/* ── Reusable bits ──────────────────────────────────────── */

function Section({
  children,
  tone = 'plain',
}: {
  children: React.ReactNode;
  tone?: 'plain' | 'muted';
}) {
  return (
    <section className={tone === 'muted' ? 'bg-slate-50 border-y border-slate-200' : ''}>
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-14 lg:py-20">{children}</div>
    </section>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-700">
      {children}
    </span>
  );
}

function Breadcrumbs({ feature }: { feature: Feature }) {
  return (
    <nav aria-label="Breadcrumbs" className="text-xs text-slate-500">
      <ol className="inline-flex items-center gap-1.5">
        <li>
          <Link to="/" className="inline-flex items-center gap-1 hover:text-slate-900 transition">
            <Home size={11} />
            Inicio
          </Link>
        </li>
        <li className="text-slate-300">/</li>
        <li>
          <Link to="/features" className="hover:text-slate-900 transition">
            Features
          </Link>
        </li>
        <li className="text-slate-300">/</li>
        <li className="text-slate-700 truncate max-w-[240px]">{feature.title}</li>
      </ol>
    </nav>
  );
}
